import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';

import { createApiClient } from './api.js';

/**
 * Pull the tenant's default theme variables so previews can render
 * against the same palette real users see.
 *
 * Reads /v1/admin/themes and picks the row with ``is_default: true``
 * (falling back to the first theme).  Returns the `variables` dict
 * unchanged — callers spread it onto a wrapping element's
 * ``style`` prop; CSS child selectors inherit the vars.
 *
 * Safe if the API call fails — you get an empty object and the
 * previews use their hard-coded fallbacks.
 */
export function useTenantTheme({ projectId = null } = {}) {
  const { getToken } = useAuth();
  const api = useMemo(
    () => createApiClient(getToken, { projectId }),
    [getToken, projectId],
  );
  const [vars, setVars] = useState(null);

  useEffect(() => {
    let alive = true;
    api('/v1/admin/themes')
      .then((resp) => {
        if (!alive) return;
        const list = Array.isArray(resp) ? resp : (resp?.items || []);
        const theme = list.find((t) => t.is_default) || list[0];
        const v = theme?.variables || theme?.light?.variables || {};
        setVars(v);
      })
      .catch(() => { if (alive) setVars({}); });
    return () => { alive = false; };
  }, [api]);

  // Return the style object (only keys starting with `--`, so
  // typos don't spill non-CSS values into inline style).
  const style = useMemo(() => {
    if (!vars) return undefined;
    const out = {};
    for (const [k, v] of Object.entries(vars)) {
      if (typeof k === 'string' && k.startsWith('--')) out[k] = v;
    }
    return out;
  }, [vars]);

  return { variables: vars, style, loaded: vars !== null };
}
